import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Wie lange die **Bildlaufleiste** nach der letzten Regung noch steht.
 *
 * „Letzte Regung" ist dabei beides: der letzte Bildlauf UND die letzte
 * Mausbewegung über dem Scroller — siehe unten.
 *
 * Gut eine halbe Sekunde, nicht eine ganze: Die Leiste beantwortet eine Frage
 * („wo bin ich in der Liste?"), und die ist beim Loslassen längst beantwortet.
 * Bei einer Sekunde stand sie nach dem Weggehen so lange herum, dass es sich
 * nach dem Doppelten anfühlte. Viel kürzer wäre auch verkehrt: Wer den Zeiger
 * beim Lesen nur kurz absetzt, soll sie nicht wegblinken sehen.
 *
 * Die Zahl ist erfühlt, nicht hergeleitet — sie darf nachjustiert werden. Es
 * gibt keine Blende, die sie abfedert: Chromium malt
 * `::-webkit-scrollbar-thumb` nativ und ignoriert `transition` darauf, die
 * Leiste ist also schlagartig weg (nachgemessen).
 */
export const SCROLLBAR_HIDE_DELAY_MS = 600

interface UseScrollbarVisibilityOptions {
  /**
   * Der Scroller. Kommt von AUSSEN, weil manche Aufrufer ihn ohnehin schon
   * halten (`ProjectPage` misst daran die Verlaufshöhen) — ein zweiter Ref auf
   * demselben Element wäre nur Ballast.
   */
  ref: React.RefObject<HTMLElement | null>
  /**
   * Ob eine Mausbewegung über dem Scroller die Leiste wachhält. Vorgabe `true`;
   * `false` gibt das frühere Verhalten (nur der Bildlauf zählt).
   */
  sustainOnPointerMove?: boolean
}

interface UseScrollbarVisibilityReturn {
  /** Ob die **Bildlaufleiste** gerade zu sehen sein soll. */
  visible: boolean
  /** An den Scroller hängen: `onScroll={onScroll}`. */
  onScroll: () => void
}

/**
 * Wann die **Bildlaufleiste** zu sehen ist — nach dem Vorbild von Apples
 * Erinnerungen (siehe „Bildlaufleiste" in CONTEXT.md):
 *
 *   1. In Ruhe unsichtbar.
 *   2. **Scrollen** blendet sie ein.
 *   3. Danach hält sie jede **Mausbewegung über diesem Scroller** weiter
 *      sichtbar. Erst wenn der Zeiger stillsteht, blendet sie aus.
 *
 * Die Bewegung ist also kein Auslöser, sondern ein Lebenszeichen: Sie sagt
 * „ich bin noch dran". Wer nur über die Liste fährt, ohne je gescrollt zu
 * haben, bekommt nichts zu sehen — sonst flackerte die Leiste bei jedem
 * Überstreichen auf.
 *
 * **Bewusst am Container und nicht am `window`:** Der Zeiger soll die Leiste
 * wachhalten, über der er steht, nicht die jeder anderen Fläche der App. Wer
 * die Maus am anderen Ende des Fensters bewegt, ist mit dieser Liste fertig.
 * Nebenbei bleibt so die Zahl der Listener klein.
 *
 * Getrennt von `useAutoScrollbar`, weil es zwei Dinge sind: ob die Leiste zu
 * sehen ist, und ob oben/unten ein Verlauf eingeblendet wird. Fünf Scroller
 * wollen das Erste, nur zwei auch das Zweite.
 *
 * @example
 * const { visible, onScroll } = useScrollbarVisibility({ ref: scrollRef })
 * <div ref={scrollRef} className={visible ? 'x--scroll-visible' : ''} onScroll={onScroll} />
 */
export function useScrollbarVisibility({
  ref,
  sustainOnPointerMove = true,
}: UseScrollbarVisibilityOptions): UseScrollbarVisibilityReturn {
  const [visible, setVisible] = useState(false)
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const rafRef = useRef<number | null>(null)

  /** Die Uhr von vorn. Als Ref-Funktion, damit der Listener stabil bleibt. */
  const restartHideTimer = useCallback(() => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
    hideTimerRef.current = setTimeout(() => {
      hideTimerRef.current = null
      setVisible(false)
    }, SCROLLBAR_HIDE_DELAY_MS)
  }, [])

  // Auf ein Rendern je Frame gedrosselt: `scroll` feuert weit häufiger.
  const onScroll = useCallback(() => {
    if (rafRef.current) return
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null
      setVisible(true)
      restartHideTimer()
    })
  }, [restartHideTimer])

  /*
    Das Lebenszeichen. Zwei Dinge sind hier tragend:

    • Der Listener hängt NUR, solange die Leiste sichtbar ist. Sonst liefe bei
      jeder Mausbewegung über die App ein Handler mit, und zwar je Scroller
      einer.
    • Er setzt KEIN `setState`. Verlängert wird allein der Zeitgeber; der
      React-Zustand steht bereits auf `true` und darf nicht je Bewegung ein
      Rendern auslösen.
  */
  useEffect(() => {
    if (!visible || !sustainOnPointerMove) return
    const el = ref.current
    if (!el) return

    el.addEventListener('pointermove', restartHideTimer, { passive: true })
    return () => el.removeEventListener('pointermove', restartHideTimer)
  }, [visible, sustainOnPointerMove, ref, restartHideTimer])

  useEffect(() => () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
  }, [])

  return { visible, onScroll }
}
